import { HabitSchedule, ScheduleEntryState, Timestamp } from '@griply/types';
import {
  addDays,
  addMilliseconds,
  addMonths,
  addWeeks,
  addYears,
  endOfDay,
  isAfter,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns';

import { weekdaysNumberMap } from '../constants';
import { getScheduleTimeframeCompletion } from '../get-schedule-timeframe-completion';
import { Frequency, RRule } from '../lib/rrule';
import {
  getPlannedTimeframeDueDateBySchedule,
  getPlannedTimeframeOccurrences,
} from './get-planned-timeframe-due-date-by-schedule';
import { getScheduleTimeframeByDate } from './get-schedule-timeframe-by-date';
import { isScheduleDue } from './is-schedule-due';
import { isScheduleOverdue } from './is-schedule-overdue';
import { Options } from './types';

export const getRruleBaseDueDateBySchedule = (
  schedule: HabitSchedule,
  { today, weekStartsOn }: Options,
) => {
  if (isScheduleDue(schedule, { today, weekStartsOn })) {
    return endOfDay(today);
  }

  // check if schedule is overdue, and if so, return the date it is overdue from
  const overdueDate = isScheduleOverdue(schedule, { today, weekStartsOn });
  if (overdueDate) {
    return overdueDate;
  }
  // if not,
  // get the current timeframe completions
  const {
    completions: completionsCurrentTimeframe,
    total: totalAllowedCompletionsCurrentTimeframe,
  } = getScheduleTimeframeCompletion(schedule, {
    referenceDate: today,
    weekStartsOn,
  });

  // get the rrule from the schedule
  let rrule = RRule.fromString(schedule.rrule.format);

  const lastEntryDate = schedule.entries.reduce<Timestamp | undefined>(
    (acc, { state, value, date }) => {
      if (
        ((state === ScheduleEntryState.Complete && !!value) ||
          state !== ScheduleEntryState.Complete) &&
        (!acc || isAfter(date, acc))
      ) {
        return date;
      }

      return acc;
    },
    undefined,
  );

  // get next timeframe startDate if current timeframe is completed
  const nextTimeframeStartDate =
    completionsCurrentTimeframe.length >=
    totalAllowedCompletionsCurrentTimeframe
      ? rrule.options.freq === Frequency.YEARLY
        ? startOfDay(addYears(startOfYear(today), 1))
        : rrule.options.freq === Frequency.MONTHLY
          ? startOfDay(addMonths(startOfMonth(today), 1))
          : startOfDay(
              startOfWeek(addWeeks(today, 1), {
                weekStartsOn: weekdaysNumberMap[weekStartsOn],
              }),
            )
      : undefined;

  // set the first next option from the latest skip, latest completion, next timeframe start date or today (depending on which is last)
  const startDateToCheckFrom = new Date(
    Math.max(
      startOfDay(addDays(today, 1)).getTime(),
      ...(nextTimeframeStartDate ? [nextTimeframeStartDate.getTime()] : []),
      ...(lastEntryDate
        ? [startOfDay(addDays(lastEntryDate, 1)).getTime()]
        : []),
    ),
  );

  rrule = new RRule({
    ...rrule.origOptions,
    dtstart: startOfDay(schedule.startDate),
  });

  const afterDue = rrule.after(startDateToCheckFrom, true);
  return afterDue ? endOfDay(afterDue) : undefined;
};

export const getRruleDueDateBySchedule = (
  schedule: HabitSchedule,
  { today, weekStartsOn }: Options,
) => {
  // check current timeframe on planned occurrences
  const hasPlannedOccurrences = !!getPlannedTimeframeOccurrences(schedule, {
    weekStartsOn,
    today,
  }).length;
  const plannedOccurrenceDate = hasPlannedOccurrences
    ? getPlannedTimeframeDueDateBySchedule(schedule, {
        weekStartsOn,
        today,
      })
    : undefined;

  // When there is no plannedOccurrence in the current timeframe, check if the previous timeframe has overdue planned occurrences
  if (!plannedOccurrenceDate) {
    // check if previous timeframe has overdue planned occurrences
    const timeframe = getScheduleTimeframeByDate(schedule, today, weekStartsOn);
    const prevTimeframeDate = addMilliseconds(timeframe.startDate, -1);
    const hasPreviousTimeframePlannedOccurrences =
      !!getPlannedTimeframeOccurrences(schedule, {
        weekStartsOn,
        today: prevTimeframeDate,
      }).length;

    const dueDatePrevTimeframe = hasPreviousTimeframePlannedOccurrences
      ? getPlannedTimeframeDueDateBySchedule(schedule, {
          weekStartsOn,
          today: prevTimeframeDate,
        })
      : undefined;

    if (dueDatePrevTimeframe) {
      return dueDatePrevTimeframe;
    }
  }

  if (plannedOccurrenceDate) {
    return plannedOccurrenceDate;
  }

  // check the regular way of rrule due date
  return getRruleBaseDueDateBySchedule(schedule, { today, weekStartsOn });
};
